import { Auth } from "aws-amplify"
import * as aws from "aws-sdk"

import API, { IRange as Range } from "@common/API"
import DateRangeUtils from "@common/utils/DateRangeUtils"
import ProjectUtils from "@common/utils/ProjectUtils"
import { getUserOrganizationId } from "@common/OrganizationUtils"
import moment from "moment"

type HttpMethod = "GET" | "POST"

interface CacheEntry<T = unknown> {
  readonly expiresAt: number
  readonly data: T
}

type ResolverQueue<T = unknown> = Array<{
  resolve: (value: T) => void
  reject: (reason?: unknown) => void
}>

interface Debouncer<T = unknown> {
  readonly timer: ReturnType<typeof setTimeout>
  readonly queue: ResolverQueue<T>
}

const CACHE_TTL_MS = 10_000 as const
const DEBOUNCE_DELAY_MS = 500 as const

const cache = new Map<string, CacheEntry>()
const debouncers = new Map<string, Debouncer<unknown>>()

/**
 * Turns `/energy/datums?start=2025-04-23T03:14:07Z&end=…` into a stable,
 * minute‑rounded & alphabetically‑sorted query string so that otherwise‑
 * identical requests map to the same cache entry.
 */
const canonicaliseUrl = (raw: string): string => {
  // URL needs an origin to parse, so use a dummy.
  const u = new URL(raw, "https://_/")

  const params = Array.from(u.searchParams.entries())
    .map<[string, string]>(([k, v]) => {
      if (["start", "end", "cumulationDate"].includes(k.toLowerCase())) {
        const t = Date.parse(v)
        if (!Number.isNaN(t)) {
          // round down to the nearest minute
          const rounded = new Date(Math.floor(t / 60_000) * 60_000).toISOString()
          return [k, rounded]
        }
      }
      return [k, v]
    })
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([k, v]) => `${k}=${v}`)
    .join("&")

  return `${u.pathname}${params ? `?${params}` : ""}`
}

const makeKey = (method: HttpMethod, url: string, body?: unknown): string =>
  `${method}|${canonicaliseUrl(url)}${body ? `|${JSON.stringify(body)}` : ""}`

function fetchWithCacheDebounce<T = unknown>(method: HttpMethod, url: string, body?: unknown): Promise<T> {
  const key = makeKey(method, url, body)

  if (method === "GET") {
    const hit = cache.get(key)
    if (hit && hit.expiresAt > Date.now()) return Promise.resolve(hit.data as T)
  }

  const existing = debouncers.get(key) as Debouncer<T> | undefined
  if (existing) {
    return new Promise<T>((resolve, reject) => {
      existing.queue.push({ resolve, reject })
    })
  }

  const queue: ResolverQueue<T> = []
  const promise = new Promise<T>((resolve, reject) => queue.push({ resolve, reject }))

  const timer = setTimeout(() => {
    let networkCall: Promise<unknown>
    if (method === "GET") {
      networkCall = API.get(url as string)
    } else if (method === "POST") {
      networkCall = API.post(url as string, body)
    } else {
      networkCall = Promise.reject(new Error(`Unsupported method ${method}`))
    }

    networkCall
      .then((data) => {
        if (method === "GET") {
          cache.set(key, { data, expiresAt: Date.now() + CACHE_TTL_MS })
        }
        queue.forEach(({ resolve }) => resolve(data as T))
      })
      .catch((err) => queue.forEach(({ reject }) => reject(err)))
      .finally(() => debouncers.delete(key))
  }, DEBOUNCE_DELAY_MS)

  debouncers.set(key, { timer, queue } as Debouncer<unknown>)
  return promise
}

const _get = <T = unknown>(url: string) => fetchWithCacheDebounce<T>("GET", url)
const _post = <T = unknown>(url: string, body?: unknown) => fetchWithCacheDebounce<T>("POST", url, body)

const EnergyService = {
  getLastMonthReadingsUrl(): string {
    const url = API.addRangeToUrl("/energy/readings", DateRangeUtils.getRange("lastMonth"))
    return url || ""
  },

  warmCache() {
    API.cacheGet(this.getLastMonthReadingsUrl() as string)
    // @ts-ignore
    API.cacheGet("/solarnetwork/devices", API.getDefaultCacheTimeout())
  },

  /* --------------------- metadata -------------------------------- */
  getDeviceMetaData(projectId: string) {
    return _get<{ sources: unknown }>(`/solarnetwork/metadata/projects/${projectId}`).then((res) => res.sources)
  },

  getProjectNodesDeviceInfo(projectId: string, projectStart: string) {
    const range = { start: moment(projectStart), end: moment() } as Range
    let url = API.addRangeToUrl(`/energy/datums/nodes/projects/${projectId}`, range) || ""
    url = API.addParamToUrl(url, "aggregation", "day") || ""
    url = API.addParamToUrl(url, "limit", "1") || ""
    return _get(url)
  },

  /* --------------------- datums ---------------------------------- */
  getNodesDatums(
    range: Range,
    aggregation: string,
    projectId: string,
    nodeIds: string,
    sourceIds: string,
    sourceId: string,
  ) {
    let url = API.addRangeToUrl(`/energy/datums/nodes/projects/${projectId}`, range) || ""
    if (nodeIds) url += `&nodeIds=${encodeURIComponent(nodeIds)}`
    if (sourceIds) url += `&sourceIds=${encodeURIComponent(sourceIds)}`
    else if (sourceId) url += `&sourceId=${encodeURIComponent(sourceId)}`
    if (aggregation) url += `&aggregation=${aggregation}`
    return _get(url)
  },

  getMostRecentEnergyDatums() {
    return _get("/energy/datums/most-recent")
  },

  getProjectCumulativeDatums(projectId: string, cumulation: string, cumulationDate: string) {
    let url = `/energy/datums/cumulative/projects/${projectId}?cumulation=${cumulation}`
    if (cumulationDate) url += `&cumulationDate=${cumulationDate}`
    return _get(url)
  },

  getEnergyDatums(range: Range, projectIds: string[], aggregation: string) {
    let url = API.addRangeToUrl("/energy/datums", range) || ""
    if (aggregation) url += `&aggregation=${aggregation}`
    if (projectIds?.length) url += `&projectIds=${projectIds.join(",")}`
    return _get(url)
  },

  getProjectEnergyDatums(range: Range, aggregation: string, project: { code: string }) {
    let url = API.addRangeToUrl(`/energy/datums/projects/${project.code}`, range) || ""
    if (aggregation) url += `&aggregation=${aggregation}`
    return _get(url)
  },

  getProjectConsumptionDatums(range: Range, aggregation: string, projectId: string) {
    let url = API.addRangeToUrl(`/energy/datums/consumption/projects/${projectId}`, range) || ""
    if (aggregation) url += `&aggregation=${aggregation}`
    return _get(url)
  },

  getForecastConsumption(range: Range, aggregation: string) {
    let url = API.addRangeToUrl("/energy/datums/consumption/forecast", range) || ""
    if (aggregation) url += `&aggregation=${aggregation}`
    return _get(url)
  },

  getProjectForecastConsumption(range: Range, aggregation: string, projectId: string, useForecast: boolean) {
    let url = API.addRangeToUrl(`/energy/datums/consumption/forecast/projects/${projectId}`, range) || ""
    if (aggregation) url += `&aggregation=${aggregation}`
    if (useForecast) url += `&useForecast=${useForecast}`
    return _get(url)
  },

  getConsumptionCost(range: Range, aggregation: string) {
    let url = API.addRangeToUrl("/energy/datums/consumption/cost", range) || ""
    if (aggregation) url += `&aggregation=${aggregation}`
    return _get(url)
  },

  getProjectConsumptionCost(range: Range, aggregation: string, projectId: string) {
    let url = API.addRangeToUrl(`/energy/datums/consumption/cost/projects/${projectId}`, range) || ""
    if (aggregation) url += `&aggregation=${aggregation}`
    return _get(url)
  },

  /* --------------------- generation ------------------------------ */
  getExpectedGeneration(range: Range, aggregation: string, projectIds: string[]) {
    let url = API.addRangeToUrl("/energy/datums/generation/expected", range) || ""
    if (aggregation) url += `&aggregation=${aggregation}`
    if (projectIds?.length) url += `&projectIds=${projectIds.join(",")}`
    return _get(url)
  },

  getPredictedGeneration(range: Range, projectIds: number[], aggregation: string) {
    let url = API.addRangeToUrl("/energy/datums/generation/predicted", range) || ""
    if (aggregation) url += `&aggregation=${aggregation}`
    if (projectIds?.length) url += `&projectIds=${projectIds.join(",")}`
    return _get(url)
  },

  getProjectPredictedGeneration(range: Range, aggregation: string, projectId: string) {
    let url = API.addRangeToUrl(`/energy/datums/generation/predicted/projects/${projectId}`, range) || ""
    if (aggregation) url += `&aggregation=${aggregation}`
    return _get(url)
  },

  /* --------------------- irradiance / readings / status ---------- */
  getEnergyIrradiance(range: Range) {
    const url = API.addRangeToUrl("/energy/irradiance", range) || ""
    return _get(url)
  },

  getEnergyReadings(range: Range, projectIds: string[]) {
    let url = API.addRangeToUrl("/energy/readings", range) || ""
    if (projectIds?.length) url += `&projectIds=${projectIds.join(",")}`
    return _get(url)
  },

  getSiteReadings(range: Range, projectId: string, siteCode: string) {
    const url = API.addRangeToUrl(`/energy/readings/projects/${projectId}/sites/${siteCode}`, range) || ""
    return _get(url)
  },

  getSiteDatums(range: Range, projectId: string, siteCode: string, aggregation?: string) {
    let url = API.addRangeToUrl(`/energy/datums/nodes/projects/${projectId}/sites/${siteCode}`, range) || ""
    if (aggregation) url += `&aggregation=${aggregation}`
    return _get(url)
  },

  getInstantaneous(projectIds: string[]) {
    return _get(`/energy/instantaneous${projectIds?.length ? `?projectIds=${projectIds.join(",")}` : ""}`)
  },

  getStatus(projectIds: string[]) {
    return _get(`/energy/status${projectIds?.length ? `?projectIds=${projectIds.join(",")}` : ""}`)
  },

  getDevices() {
    // @ts-ignore
    return API.getFromCache("/solarnetwork/devices", API.getDefaultCacheTimeout()).then(
      // @ts-ignore
      (response: { projects: unknown }) => {
        return response.projects
      },
    )
  },

  getSolarfluxCredentials() {
    return _get("/solarnetwork/credentials/solarflux")
  },

  export(projectIds: string, range: Range, aggregate: string, format: string, sourceIds: string, consolidate: boolean) {
    let url = API.addRangeToUrl("/energy/export", range) || ""
    url += `&projectIds=${projectIds}`
    url += `&format=${format}`
    url += `&aggregate=${aggregate}`
    if (sourceIds) url += `&sourceIds=${sourceIds}`
    if (consolidate) url += `&consolidate=${consolidate}`
    return _get(url)
  },

  enableSolarFluxHyperMode(project: unknown, site: unknown, timeout: number) {
    _post("/solarnetwork/instruction", {
      path: ProjectUtils.getPath(project, site),
      params: {
        "parameters[0].name": "OpMode",
        "parameters[0].value": "hyper",
        "parameters[1].name": "Expiration",
        "parameters[1].value": timeout,
        topic: "EnableOperationalModes",
      },
    })
  },

  disableSolarFluxHyperMode(project: unknown, site: unknown) {
    _post("/solarnetwork/instruction", {
      path: ProjectUtils.getPath(project, site),
      params: {
        "parameters[0].name": "OpMode",
        "parameters[0].value": "hyper",
        topic: "DisableOperationalModes",
      },
    })
  },

  async downloadConsumption(project: { code: string }) {
    const orgId = await getUserOrganizationId()
    const credentials = await Auth.currentCredentials()

    const s3 = new aws.S3({
      apiVersion: "2013-04-01",
      credentials: Auth.essentialCredentials(credentials),
    })

    const params = {
      Bucket: process.env.REACT_APP_CACHE_BUCKET as string,
      Key: `${orgId}/consumption-cost/datums/project/${project.code}.json`,
    } as const

    // @ts-expect-error – callback signature differs
    return s3.getSignedUrlPromise("getObject", params, (_, url: string) => window.open(url))
  },
} as const

export default EnergyService
